import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ErrorIcon from "@mui/icons-material/Error";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getHeaders } from "../../utils/HeaderHelper";
import { AuthenticationContext } from "../../Context";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ImportRun {
  id: number;
  payer: string | null;
  file_type: string;
  source_file_name: string;
  status: string;
  processing_stage: string;
  run_error: string | null;
  run_id: string | null;
  total_records: number;
  records_inserted: number;
  records_failed: number;
  received_at: string;
  started_at: string | null;
  finished_at: string | null;
}

type StepState = "complete" | "active" | "error" | "warned" | "pending";

// ─── Pipeline ─────────────────────────────────────────────────────────────────

const PIPELINE_STEPS = [
  { key: "received",         label: "Received"       },
  { key: "etl_processing",   label: "ETL Processing" },
  { key: "etl_validated",    label: "Validated"      },
  { key: "rails_processing", label: "Rails Import"   },
  { key: "completed",        label: "Completed"      },
];

const BLOCKED_STATUSES = [
  "blocked",
  "duplicate_blocked",
  "threshold_blocked",
  "validation_blocked",
];

const ETL_RELEASABLE_STATUSES = [
  "duplicate_blocked",
  "threshold_blocked",
  "validation_blocked",
];

function getStepStates(status: string, processingStage: string): StepState[] {
  if (status === "completed") {
    return ["complete", "complete", "complete", "complete", "complete"];
  }
  if (status === "failed") {
    if (["rails_processing", "failed", "completed"].includes(processingStage)) {
      return ["complete", "complete", "complete", "error", "pending"];
    }
    return ["complete", "error", "pending", "pending", "pending"];
  }
  // SOM preprocessing (single-table). Preprocessing happens before ETL processing.
  if (status === "preprocessing_failed") {
    return ["complete", "error", "pending", "pending", "pending"];
  }
  if (status === "preprocessing_rejects") {
    // succeeded with a rejects file — continues, but flag the warning
    return ["complete", "warned", "pending", "pending", "pending"];
  }
  if (status === "preprocessing") {
    return ["complete", "active", "pending", "pending", "pending"];
  }
  if (processingStage === "rails_processing") {
    return ["complete", "complete", "complete", "active", "pending"];
  }
  if (status === "unblocked") {
    return ["complete", "complete", "warned", "pending", "pending"];
  }
  if (BLOCKED_STATUSES.includes(status)) {
    return ["complete", "complete", "error", "pending", "pending"];
  }
  if (status === "duplicate") {
    return ["complete", "complete", "warned", "pending", "pending"];
  }
  if (status === "validated") {
    return ["complete", "complete", "complete", "pending", "pending"];
  }
  if (status === "processing" || status === "reprocessing") {
    return ["complete", "active", "pending", "pending", "pending"];
  }
  // started / default
  return ["active", "pending", "pending", "pending", "pending"];
}

const STEP_COLORS: Record<StepState, string> = {
  complete: "#4caf50",
  active:   "#1976d2",
  error:    "#d32f2f",
  warned:   "#ed6c02",
  pending:  "#bdbdbd",
};

// ─── StepIcon ─────────────────────────────────────────────────────────────────

const StepIcon: React.FC<{ state: StepState }> = ({ state }) => {
  const color = STEP_COLORS[state];
  const sz = 18;
  if (state === "complete") return <CheckCircleIcon sx={{ fontSize: sz, color }} />;
  if (state === "active")   return <FiberManualRecordIcon sx={{ fontSize: sz, color }} />;
  if (state === "error")    return <ErrorIcon sx={{ fontSize: sz, color }} />;
  if (state === "warned")   return <WarningAmberIcon sx={{ fontSize: sz, color }} />;
  return <RadioButtonUncheckedIcon sx={{ fontSize: sz, color }} />;
};

// ─── StatusPipeline ───────────────────────────────────────────────────────────

const StatusPipeline: React.FC<{ run: ImportRun }> = ({ run }) => {
  const stepStates = getStepStates(run.status, run.processing_stage);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 310 }}>
      {PIPELINE_STEPS.map((step, i) => (
        <React.Fragment key={step.key}>
          <Tooltip
            title={
              (stepStates[i] === "error" || stepStates[i] === "warned") && run.run_error
                ? `${step.label}: ${run.run_error}`
                : step.label
            }
          >
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "default" }}>
              <StepIcon state={stepStates[i]} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.6rem",
                  color: STEP_COLORS[stepStates[i]],
                  whiteSpace: "nowrap",
                  mt: 0.25,
                  fontWeight: ["active", "error", "warned"].includes(stepStates[i]) ? 700 : 400,
                }}
              >
                {step.label}
              </Typography>
            </Box>
          </Tooltip>

          {i < PIPELINE_STEPS.length - 1 && (
            <Box
              sx={{
                flex: 1,
                height: 2,
                minWidth: 14,
                bgcolor: stepStates[i] === "complete" ? STEP_COLORS.complete : STEP_COLORS.pending,
                mt: "8px",
                mx: 0.5,
              }}
            />
          )}
        </React.Fragment>
      ))}
    </Box>
  );
};

// ─── Status chip config ───────────────────────────────────────────────────────

type ChipColor = "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

const STATUS_CONFIG: Record<string, { label: string; color: ChipColor }> = {
  started:               { label: "Started",              color: "default"  },
  processing:            { label: "Processing",           color: "info"     },
  reprocessing:          { label: "Reprocessing",         color: "info"     },
  validated:             { label: "Validated",            color: "success"  },
  duplicate:             { label: "Duplicate",            color: "warning"  },
  duplicate_blocked:     { label: "Duplicate Blocked",    color: "warning"  },
  threshold_blocked:     { label: "Threshold Blocked",    color: "warning"  },
  validation_blocked:    { label: "Validation Blocked",   color: "error"    },
  blocked:               { label: "Blocked",              color: "error"    },
  unblocked:             { label: "Unblocked",            color: "warning"  },
  completed:             { label: "Completed",            color: "success"  },
  failed:                { label: "Failed",               color: "error"    },
  // SOM single-table preprocessing statuses
  preprocessing:         { label: "Preprocessing",        color: "info"     },
  preprocessing_rejects: { label: "Preprocessed w/ Rejects", color: "warning" },
  preprocessing_failed:  { label: "Preprocessing Failed", color: "error"    },
};

// ─── Main component ───────────────────────────────────────────────────────────

const ImportRunsIndex: React.FC = () => {
  const authenticationSetting = React.useContext(AuthenticationContext);

  const [importRuns, setImportRuns]       = React.useState<ImportRun[]>([]);
  const [loading, setLoading]             = React.useState(true);
  const [fileTypeFilter, setFileTypeFilter] = React.useState("all");
  const [statusFilter, setStatusFilter]   = React.useState("all");
  const [confirmOpen, setConfirmOpen]     = React.useState(false);
  const [selectedRun, setSelectedRun]     = React.useState<ImportRun | null>(null);
  const [unblocking, setUnblocking]       = React.useState(false);
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLButtonElement | null>(null);
  const [popoverRun, setPopoverRun]       = React.useState<ImportRun | null>(null);
  const [toast, setToast] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const showToast = (message: string, severity: "success" | "error") =>
    setToast({ open: true, message, severity });

  const fetchRuns = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/admin/import_runs", {
        headers: getHeaders(authenticationSetting.csrfToken),
      });
      const data = await res.json();
      if (data.success) {
        setImportRuns(data.resource || []);
      } else {
        showToast(data.message || "Failed to load import runs.", "error");
      }
    } catch {
      showToast("Network error loading import runs.", "error");
    } finally {
      setLoading(false);
    }
  }, [authenticationSetting]);

  React.useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const handleUnblockClick = (run: ImportRun) => {
    setSelectedRun(run);
    setConfirmOpen(true);
  };

  const handleCancelUnblock = () => {
    setConfirmOpen(false);
    setSelectedRun(null);
  };

  const handleConfirmUnblock = async () => {
    if (!selectedRun) return;
    setUnblocking(true);
    try {
      const res = await fetch(`/admin/import_runs/${selectedRun.id}/unblock`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
      });
      const data = await res.json();
      if (data.success) {
        showToast(data.message, "success");
        await fetchRuns();
      } else {
        showToast(data.message || "Failed to unblock.", "error");
      }
    } catch {
      showToast("Network error while unblocking. Please try again.", "error");
    } finally {
      setUnblocking(false);
      setConfirmOpen(false);
      setSelectedRun(null);
    }
  };

  const filteredRuns = React.useMemo(() => {
    return importRuns.filter((r) => {
      if (fileTypeFilter !== "all" && r.file_type !== fileTypeFilter) return false;
      if (statusFilter === "blocked"     && !BLOCKED_STATUSES.includes(r.status)) return false;
      if (statusFilter === "completed"   && r.status !== "completed")             return false;
      if (statusFilter === "failed"      &&
        !["failed", "preprocessing_failed"].includes(r.status)
      ) return false;
      if (statusFilter === "in_progress" &&
        !["started", "processing", "reprocessing", "validated", "unblocked",
          "preprocessing", "preprocessing_rejects"].includes(r.status)
      ) return false;
      return true;
    });
  }, [importRuns, fileTypeFilter, statusFilter]);

  const formatDate = (d: string | null) =>
    d ? new Date(d).toLocaleString() : "—";

  return (
    <div className="coreTeam">
      <Grid
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        className="container2"
      >
        <Grid item xs={12} style={{ paddingRight: 32 }}>
          <p className="search-form-label">IMPORT RUNS</p>
          <p style={{ fontSize: "0.875rem", color: "#fff", marginBottom: 16 }}>
            All recent import runs are listed below with their processing pipeline status.
            Blocked runs can be reviewed and released for re-processing.
          </p>

          {/* ── Filters ── */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>File Type</InputLabel>
              <Select
                value={fileTypeFilter}
                label="File Type"
                onChange={(e) => setFileTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="eligibility">Eligibility</MenuItem>
                <MenuItem value="claim">Claim</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="blocked">Blocked</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>

            <Button variant="outlined" size="small" onClick={fetchRuns} disabled={loading}>
              Refresh
            </Button>
          </Box>

          {/* ── Table ── */}
          {loading ? (
            <Grid container justifyContent="center" style={{ marginTop: 32 }}>
              <CircularProgress />
            </Grid>
          ) : filteredRuns.length === 0 ? (
            <Alert severity="info">No import runs match the current filters.</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>ID</strong></TableCell>
                    <TableCell><strong>File Name</strong></TableCell>
                    <TableCell><strong>Payer</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Status Pipeline</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Records</strong></TableCell>
                    <TableCell><strong>Received</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRuns.map((run) => {
                    const statusCfg = STATUS_CONFIG[run.status] || {
                      label: run.status,
                      color: "default" as ChipColor,
                    };
                    return (
                      <TableRow key={run.id} hover>
                        <TableCell>{run.id}</TableCell>

                        <TableCell sx={{ maxWidth: 180 }}>
                          <Tooltip title={run.source_file_name}>
                            <Box
                              component="span"
                              sx={{
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {run.source_file_name}
                            </Box>
                          </Tooltip>
                        </TableCell>

                        <TableCell>{run.payer || "—"}</TableCell>

                        <TableCell>
                          <Chip label={run.file_type} size="small" variant="outlined" />
                        </TableCell>

                        <TableCell sx={{ py: 1.5 }}>
                          <StatusPipeline run={run} />
                        </TableCell>

                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Chip
                              label={statusCfg.label}
                              color={statusCfg.color}
                              size="small"
                            />
                            {run.run_error && (
                              <Tooltip title="View block reason">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    setPopoverAnchor(e.currentTarget);
                                    setPopoverRun(run);
                                  }}
                                  sx={{ color: "text.secondary", p: 0.25 }}
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>

                        <TableCell sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                          <Box>Total: {run.total_records.toLocaleString()}</Box>
                          {run.records_inserted > 0 && (
                            <Box sx={{ color: "success.main" }}>
                              ✓ {run.records_inserted.toLocaleString()} inserted
                            </Box>
                          )}
                          {run.records_failed > 0 && (
                            <Box sx={{ color: "error.main" }}>
                              ✗ {run.records_failed.toLocaleString()} failed
                            </Box>
                          )}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatDate(run.received_at)}
                        </TableCell>

                        <TableCell>
                          {ETL_RELEASABLE_STATUSES.includes(run.status) && (
                            <Button
                              variant="contained"
                              color="warning"
                              size="small"
                              onClick={() => handleUnblockClick(run)}
                            >
                              Unblock
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Block reason popover ── */}
          <Popover
            open={Boolean(popoverAnchor)}
            anchorEl={popoverAnchor}
            onClose={() => { setPopoverAnchor(null); setPopoverRun(null); }}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Box sx={{ p: 2, maxWidth: 420 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Block Reason — Run #{popoverRun?.id}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}>
                {popoverRun?.run_error || "No details available."}
              </Typography>
            </Box>
          </Popover>

          {/* ── Confirm dialog ── */}
          <Dialog open={confirmOpen} onClose={handleCancelUnblock}>
            <DialogTitle>Confirm Unblock</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Release import run <strong>#{selectedRun?.id}</strong> (
                {selectedRun?.source_file_name}) and re-submit for ETL processing?
                <br /><br />
                The ETL will skip the <strong>{selectedRun?.status}</strong> check and continue
                from the next step. A new import run will be created for tracking.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCancelUnblock} disabled={unblocking}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmUnblock}
                color="warning"
                variant="contained"
                disabled={unblocking}
              >
                {unblocking && <CircularProgress size={16} sx={{ mr: 1 }} />}
                Confirm Unblock
              </Button>
            </DialogActions>
          </Dialog>

          {/* ── Toast ── */}
          <Snackbar
            open={toast.open}
            autoHideDuration={6000}
            onClose={() => setToast((t) => ({ ...t, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={toast.severity}
              onClose={() => setToast((t) => ({ ...t, open: false }))}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </Grid>
      </Grid>
    </div>
  );
};

export default ImportRunsIndex;
